import { useEffect, useState } from 'react';
import { tg } from './telegram';

export type ThemeMode = 'light' | 'dark' | 'system';

const KEY = 'gruzbot_theme';

/**
 * Тема оформления.
 *
 * По умолчанию — «системная»: в Telegram это тема клиента, в браузере —
 * настройка операционной системы. Навязывать светлую тему водителю, который
 * ночью держит телефон в кабине, — плохая идея.
 */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;

  // Telegram сообщает свою тему явно и раньше, чем сработает медиазапрос.
  if (tg?.colorScheme === 'dark' || tg?.colorScheme === 'light') {
    return tg.colorScheme;
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function apply(mode: ThemeMode) {
  const actual = resolveTheme(mode);
  document.documentElement.dataset.theme = actual;

  // Цвет строки состояния браузера: без него на мобильном остаётся белая
  // полоса над тёмным интерфейсом.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', actual === 'dark' ? '#0f1115' : '#f5f6f8');
  }
}

function stored(): ThemeMode {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(stored);

  useEffect(() => {
    apply(mode);
    localStorage.setItem(KEY, mode);

    if (mode !== 'system') return;

    // В системном режиме следим за сменой темы на лету: пользователь может
    // переключить её, не закрывая приложение.
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    mq?.addEventListener('change', onChange);
    tg?.onEvent?.('themeChanged', onChange);

    return () => {
      mq?.removeEventListener('change', onChange);
      tg?.offEvent?.('themeChanged', onChange);
    };
  }, [mode]);

  /** Перебор по кругу: система → светлая → тёмная. */
  const cycle = () =>
    setMode((m) => (m === 'system' ? 'light' : m === 'light' ? 'dark' : 'system'));

  return { mode, setMode, cycle, actual: resolveTheme(mode) };
}

/** Тема применяется до первого рендера, иначе экран моргает белым. */
export function initTheme() {
  apply(stored());
}
